import React, { useState, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { CmsSsrComponentProps } from '@/types/cms';
import { getDeliveryDate } from '@/utils/common/porder';
import { getHomePageDataByUrl } from '@/api/content';
import useTranslation from 'next-translate/useTranslation';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import ProductCard from '@/components/common/ProductCard/VerticalItemLine';
import { AddToCartReferType } from '@/constants/track';
import Header from '../component/Layout/header';
import ProductCardSkeleton from '@/components/common/Skeleton/product-card-ssr';
import { useComponentTheme } from '../hooks/useComponentTheme';
import { relativeLinkIfQAEnv } from '../tools/cms-link';
import More from '../component/Layout/more';
import Container from './Container';
import { useInView } from 'react-intersection-observer';
import DataSourceEmpty from './DataSourceEmpty';

const ProductLine: React.FC<CmsSsrComponentProps> = props => {
  const {
    componentKey,
    componentInstanceKey,
    componentPosition,
    properties,
    trackingContext,
    fetchUrl,
    componentDataSourceKey,
    staticRes,
    top,
    onDataSourceError,
    isPreview
  } = props;
  const { lang } = useTranslation('common');
  const { titleStyle, moreLinkStyle } = useComponentTheme({ properties, pageTheme: 'string' });
  const eventKey = properties?.event_key || componentKey;
  const [products, setProducts] = useState<ProductType[]>(Array.from({ length: 3 }, (_, index) => ({ id: index + 1 })));
  const [load, setLoad] = useState<boolean>(false);

  const { ref, inView, entry } = useInView({
    threshold: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await getHomePageDataByUrl(fetchUrl, {
        lang,
        dataobject_key: componentDataSourceKey
      });
      if (res?.result && Array.isArray(res?.object?.products) && res?.object?.products?.length > 0) {
        setProducts(res?.object?.products ?? []);
      } else {
        setProducts([]);
        onDataSourceError?.();
      }
      setLoad(true);
    } catch (error) {
      setProducts([]);
      onDataSourceError?.();
      setLoad(true);
    }
  }, [componentDataSourceKey, fetchUrl, lang, onDataSourceError]);

  useEffect(() => {
    if (Array.isArray(staticRes?.products) && staticRes?.products?.length > 0) {
      setProducts(staticRes?.products ?? []);
      setLoad(true);
      return;
    }
    if (inView) fetchData();
  }, [inView, staticRes]);

  if (load && !products.length)
    return (
      <DataSourceEmpty componentInstanceKey={componentInstanceKey} componentKey={componentKey}>
        <div className="h-5" />
      </DataSourceEmpty>
    );

  return (
    <Container
      componentKey={componentKey}
      componentInstanceKey={componentInstanceKey}
      properties={properties}
      ref={ref}
      className="py-2"
      top={top}>
      {/* header */}
      {(properties.title || properties.more_link) && (
        <Header
          className="py-2"
          title={properties.title}
          moreLink={properties.more_link}
          moreLinkStyle={moreLinkStyle}
          subTitle={properties.sub_title}
          titleStyle={titleStyle}
          trackingParams={{
            mod_nm: eventKey,
            mod_pos: `${componentPosition}`,
            ctx: trackingContext
          }}
        />
      )}
      <div className="w-full">
        <Swiper
          slidesPerView="auto"
          freeMode
          spaceBetween={4}
          slidesOffsetAfter={10}
          slidesOffsetBefore={10}
          lazy
          className="items-stretch">
          {!!products?.length &&
            products?.map((item, index) => {
              return (
                <SwiperSlide
                  key={`${item.id}-${index}`}
                  className={twMerge('!w-[150px] flex-none !h-auto flex items-stretch')}>
                  {load ? (
                    // <LazyView rootMargin="6px 0px 0px 6px" skeleton={index > 2 && <ProductCardSkeleton />}>
                    <ProductCard
                      addCartParams={{
                        referType: AddToCartReferType.normal,
                        deliveryDate: getDeliveryDate()
                      }}
                      positionInfoT2={{
                        modSecPos: {
                          mod_nm: eventKey,
                          mod_pos: componentPosition
                        },
                        prodPos: index,
                        context: {
                          tag_key: item?.entrance_tag?.tag_key,
                          tag_name: item?.entrance_tag?.tag_name,
                          ...trackingContext
                        }
                      }}
                      data={item}
                      showAddBtn={!isPreview}
                    />
                  ) : (
                    // </LazyView>
                    <ProductCardSkeleton className="h-[291px]" />
                  )}
                </SwiperSlide>
              );
            })}
          {!!properties?.more_link && (
            <SwiperSlide className="!w-[150px] !h-auto flex items-center justify-center">
              <More
                moreLink={relativeLinkIfQAEnv(properties?.more_link)}
                trackingParams={{
                  mod_nm: eventKey,
                  mod_pos: `${componentPosition}`,
                  ctx: trackingContext
                }}
              />
            </SwiperSlide>
          )}
        </Swiper>
      </div>
    </Container>
  );
};

export default ProductLine;
